import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { Box, Button } from '@mui/material';
import AdminSlideDrawer from '@sharedCR/common/AdminSlideDrawer';
import FormGenerator from '@sharedCR/common/formGenerator';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { COMMON_REQUEST_DATETIME_FORMAT } from 'constants/date';
import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';

import { formContentStyles } from './PreInvestorConditionDrawer.styles';
import { formatConditionDate } from './PreInvestorConditionDrawer.utils';
import {
  PRE_CONDITION_DATES_FORM_CONFIG,
  PRE_CONDITION_TEXT_FORM_CONFIG,
  PRE_INVESTOR_CONDITION_FORM_CONFIG,
} from './PreInvestorConditionModal.config';
import { PreInvestorConditionsTestIds } from './PreInvestorConditions.testIds';

const PreInvestorConditionDrawer = ({ open, condition, onSave, onClose }) => {
  const applicationData = useMemo(
    () => ({
      conditionText: condition?.conditionText ?? '',
      dateAdded: condition?.dateAdded ?? dayjs().format(COMMON_REQUEST_DATETIME_FORMAT),
      dateResponded: condition?.dateResponded ?? null,
      dateCleared: condition?.dateCleared ?? null,
    }),
    [condition],
  );

  const { initialFormValues, fieldsConfig, observableFieldsNames, schema } = useMemo(
    () => getConfigInfo({ applicationData, config: PRE_INVESTOR_CONDITION_FORM_CONFIG }),
    [applicationData],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setInForm,
    validateField,
    validateForm,
    setSchema,
    setForm,
    setInitialFormValues,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const onSaveCondition = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    const recordData = {
      conditionText: form.conditionText ?? '',
      dateAdded: formatConditionDate(form.dateAdded),
      dateResponded: formatConditionDate(form.dateResponded),
      dateCleared: formatConditionDate(form.dateCleared),
    };

    onSave(recordData, condition?.id);
    onClose();
  }, [form, onSave, condition?.id, onClose, validateForm]);

  const drawerActions = useMemo(
    () => (
      <Button
        data-testid={PreInvestorConditionsTestIds.DRAWER_SAVE_BUTTON}
        onClick={onSaveCondition}
      >
        Save
      </Button>
    ),
    [onSaveCondition],
  );

  const formGeneratorProps = useMemo(
    () => ({
      form,
      handleChange,
      setInForm,
      validateField,
      validationErrors,
    }),
    [form, handleChange, setInForm, validateField, validationErrors],
  );

  const drawerContent = useMemo(
    () => (
      <Box sx={formContentStyles}>
        <FormGenerator
          config={PRE_CONDITION_TEXT_FORM_CONFIG}
          numberOfColumns={1}
          {...formGeneratorProps}
        />
        <FormGenerator
          config={PRE_CONDITION_DATES_FORM_CONFIG}
          numberOfColumns={3}
          {...formGeneratorProps}
        />
      </Box>
    ),
    [formGeneratorProps],
  );

  useEffect(() => {
    setForm(initialFormValues);
    setInitialFormValues(initialFormValues);
  }, [setForm, setInitialFormValues, initialFormValues]);

  useEffect(() => {
    setSchema(schema);
  }, [schema, setSchema]);

  if (!open) {
    return null;
  }

  return (
    <AdminSlideDrawer
      actions={drawerActions}
      content={drawerContent}
      open={open}
      title={condition ? 'Edit Condition' : 'Add Condition'}
      onClose={onClose}
    />
  );
};

PreInvestorConditionDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  condition: PropTypes.shape({
    id: PropTypes.string,
    conditionText: PropTypes.string,
    dateAdded: PropTypes.string,
    dateResponded: PropTypes.string,
    dateCleared: PropTypes.string,
  }),
};

export default memo(PreInvestorConditionDrawer);
